import { useCallback, useState } from "react";
import { dispatchTrackingEvent } from "src/utils/analytics";
import { mapFilterStateChangeToLegacyEventValues as mapFilterStateChangeToLegacyEvents } from "src/utils/analytics/legacyMappings";
import type {
  Hit,
  IndexUiState,
  IndexUiState as SearchState,
  UiState
} from "instantsearch.js";
import deepmerge from "deepmerge";
import {
  PromoAlgoliaCodeMapping,
  searchStateToSerializableParams
} from "./utils";
import type { SendEventForHits } from "instantsearch.js/es/lib/utils";
import { envVariableTrue } from "src/gatsby-config/hosting-config";
import { getVisitorAuid } from "src/utils/analytics/tracking";
import { stringPresent } from "src/utils/strings";
import aa from "search-insights";
import { cruiseSearchIndexName } from "src/algolia";
import { useAlgoliaAnalyticsParams } from "./AlgoliaAnalyticsProvider";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { InstantSearchProps } from "react-instantsearch";

type UseSearchAnalyticsReturnValues = {
  onSearchStateChange: (updatedSearchState: UiState) => void;
};

const debouncedFilterKeys = ["query"];

const splitDebouncedEvents = (events) =>
  events.reduce(
    (result, event) =>
      debouncedFilterKeys.includes(event.filterKey)
        ? { ...result, debounce: [...result.debounce, event] }
        : { ...result, fireNow: [...result.fireNow, event] },
    { fireNow: [], debounce: [] }
  );

const dispatchTrackingEvents = (eventName: string, events) => {
  for (const event of events)
    dispatchTrackingEvent(eventName, {
      value: event.value
    });
};

type OnStateChangeParams = Parameters<
  NonNullable<InstantSearchProps["onStateChange"]>
>["0"];

export const useSearchAnalytics = (
  eventName: string,
  promoAlgoliaCodeMapping: PromoAlgoliaCodeMapping,
  onSearchStateChange?: (searchState: UiState) => void
) => {
  const { onSearchStateChange: analyticsOnSearchStateChange } =
    useInternalSearchAnalytics(eventName, promoAlgoliaCodeMapping, undefined);

  const _onStateChange: InstantSearchProps["onStateChange"] = useCallback(
    (params: OnStateChangeParams) => {
      const { uiState, setUiState } = params;

      setUiState(uiState);
      analyticsOnSearchStateChange(uiState);

      if (onSearchStateChange) onSearchStateChange(uiState);
    },
    [analyticsOnSearchStateChange, onSearchStateChange]
  );

  return { onStateChange: _onStateChange };
};

function useInternalSearchAnalytics(
  eventName: string,
  promoAlgoliaCodeMapping: PromoAlgoliaCodeMapping,
  onSearchStateChange?: (searchState: UiState) => void
): UseSearchAnalyticsReturnValues {
  const [prevSearchState, setPrevSearchState] = useState<
    SearchState | undefined
  >(undefined);
  const [debounceTimeout, setDebounceTimeout] = useState<
    NodeJS.Timeout | undefined
  >(undefined);

  const extendedOnSearchStateChange = (state: UiState) => {
    const prevState = prevSearchState;
    const mergedSearchState = Object.keys(state).reduce((acc, algoliaKey) => {
      return deepmerge(acc, state[algoliaKey]);
    }, {} as IndexUiState);
    const searchState = searchStateToSerializableParams(
      mergedSearchState,
      promoAlgoliaCodeMapping
    );
    setPrevSearchState(searchState);

    const events = splitDebouncedEvents(
      mapFilterStateChangeToLegacyEvents(searchState, prevState || {})
    );

    dispatchTrackingEvents(eventName, events.fireNow);

    if (events.debounce.length) {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      setDebounceTimeout(
        setTimeout(() => {
          dispatchTrackingEvents(eventName, events.debounce);
        }, 400)
      );
    }

    if (!onSearchStateChange) return;
    onSearchStateChange(state);
  };

  return {
    onSearchStateChange: extendedOnSearchStateChange
  };
}

export const isAlgoliaAnalyticsEnabled = () =>
  envVariableTrue(process.env.GATSBY_ALGOLIA_ENABLE_ANALYTICS);

export const algoliaUserToken = () => {
  const isTrackingEnabled = isAlgoliaAnalyticsEnabled();
  const visitorId = getVisitorAuid();
  const isVisitorIdPresent = stringPresent(visitorId);
  return isTrackingEnabled && isVisitorIdPresent ? visitorId : undefined;
};

export const trackAlgoliaResultClick = (
  algoliaSendEvent: SendEventForHits,
  hit: Hit,
  name: string
) => {
  if (isAlgoliaAnalyticsEnabled()) algoliaSendEvent("click", hit, name);
};

export const useAlgoliaConversionTracking = () => {
  const { algoliaArea } = useGeolocation();
  const index = algoliaArea ? cruiseSearchIndexName(algoliaArea) : undefined;
  const { objectID, queryID } = useAlgoliaAnalyticsParams();
  const onRAQClick = useCallback(() => {
    if (!queryID || !index) return;

    sendAlgoliaConversionEvent("RAQ", index, {
      queryID,
      objectID
    });
  }, [objectID, queryID, index]);

  const onWebQuotelick = useCallback(
    (originalOnClick?: () => void) => {
      if (!queryID || !index) return;

      // TODO: change RAQ to WebQuote and reconfigure Algolia after A/B test
      sendAlgoliaConversionEvent("RAQ", index, {
        queryID,
        objectID
      });

      originalOnClick?.();
    },
    [objectID, queryID, index]
  );

  const onECommerceClick = useCallback(() => {
    if (!queryID || !index) return;

    sendAlgoliaConversionEvent("Book Now", index, {
      queryID,
      objectID
    });
  }, [objectID, queryID, index]);

  return { onRAQClick, onECommerceClick, onWebQuotelick };
};

const sendAlgoliaConversionEvent = (
  eventName: string,
  indexName: string,
  args: {
    queryID: string;
    objectID: string | null | undefined;
  }
) => {
  const userToken = algoliaUserToken();

  aa("init", {
    appId: process.env.GATSBY_ALGOLIA_APP_ID,
    apiKey: process.env.GATSBY_ALGOLIA_API_KEY,
    useCookie: false
  });

  aa("convertedObjectIDsAfterSearch", {
    eventName: eventName,
    userToken: userToken,
    index: indexName,
    queryID: args.queryID,
    objectIDs: args.objectID ? [args.objectID] : []
  });
};
